/**
 * StrategyV2ScreenFlowCoordinator - V2 coordinator for strategy block screens
 *
 * Fork of V2ScreenFlowCoordinator adapted for strategy context:
 * - Constructor: (projectId, strategyId, blockId) instead of (projectId, onboardingId, versionId)
 * - Screen service: StrategyStepNodeService (constructor-based path, no per-call IDs)
 * - Launch screen: block.firstScreenId via StrategyBlockService (not OnboardingService)
 *
 * Reuses: v2ScreenRegistry, v2ScreenAdapter, v2TransitionManager (all stateless/clearable singletons)
 */

import type { StepNode } from '@segmently/schema-models/v2';
import type { ReactFlowCanvasHandle, EntityMove, PositionOnConfigurator } from '@modules/reactFlow/types/flow-canvas-types';
import type { FlowCoordinator, CreateEntityResult, CreateTransitionInput, LoadingCallbacks } from '../shared/FlowCoordinator';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';
import { v2ScreenRegistry } from '../onboarding/V2ScreenRegistry';
import { v2ScreenAdapter, type FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { v2TransitionManager } from '../onboarding/V2TransitionManager';
import { clearAllStepEdges } from '@modules/reactFlow/utils/transformers/V2EdgeCleaner';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';
import { buildV2ActionHandles } from '@modules/reactFlow/services/shared/V2ActionHandleResolver';
import { StrategyStepNodeService } from '@modules/firebase-core/services/StrategyStepNodeService';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import { simplifiedV2Mapper } from '@modules/screen-utils/services/SimplifiedV2Mapper';

interface EdgeData {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  label: string;
  type: 'transition';
  animated: boolean;
  data: {
    sourceEntityId: string;
    targetEntityId: string;
    sourceActionKey: string;
    rules?: unknown[];
    onEdgeClick?: (edgeId: string, edgeData: any) => void;
  };
}

/**
 * StrategyV2ScreenFlowCoordinator
 *
 * Implements: FlowCoordinator<FlowStepNode, StrategyStepNodeService>
 */
export class StrategyV2ScreenFlowCoordinator implements FlowCoordinator<FlowStepNode, StrategyStepNodeService> {
  private initialized: boolean = false;
  private screenService: StrategyStepNodeService | null = null;
  private blockService: StrategyBlockService | null = null;
  private projectId: string;
  private strategyId: string;
  private blockId: string;
  private canvasHandle: ReactFlowCanvasHandle | null = null;
  private loadingCallbacks?: LoadingCallbacks;
  private launchScreenId: string | null = null;
  private onLaunchScreenChanged?: (newLaunchScreenId: string) => void;

  private templateSource: ((templateId: string) => StepNode | null) | null = null;
  public targetLocales: string[] = [];
  public defaultLanguage: string = 'en-US';

  constructor(projectId: string, strategyId: string, blockId: string) {
    this.projectId = projectId;
    this.strategyId = strategyId;
    this.blockId = blockId;
    this.screenService = new StrategyStepNodeService(projectId, strategyId, blockId);
    this.blockService = new StrategyBlockService(projectId, strategyId);
    this.initialized = true;
    console.log('[StrategyV2Coordinator] Initialized:', { projectId, strategyId, blockId });
  }

  // ============================================================================
  // GETTERS
  // ============================================================================

  public getProjectId(): string { return this.projectId; }
  public getStrategyId(): string { return this.strategyId; }
  public getBlockId(): string { return this.blockId; }
  public getScreenService(): StrategyStepNodeService | null { return this.screenService; }

  public setTemplateSource(getter: (templateId: string) => StepNode | null): void {
    this.templateSource = getter;
  }

  public setLocaleSettings(targetLocales: string[], defaultLanguage: string): void {
    this.targetLocales = targetLocales;
    this.defaultLanguage = defaultLanguage;
  }

  public setOnLaunchScreenChanged(callback: (newLaunchScreenId: string) => void): void {
    this.onLaunchScreenChanged = callback;
  }

  // ============================================================================
  // INITIALIZATION & LIFECYCLE
  // ============================================================================

  async loadScreensFromFirebase(): Promise<void> {
    if (!this.screenService) {
      console.warn('[StrategyV2Coordinator] Cannot load screens - service not initialized');
      return;
    }

    console.log('[StrategyV2Coordinator] Loading screens from Firebase...');
    console.log(`  Path: ${this.screenService.collectionPath}`);

    const steps = await this.screenService.listScreens();
    console.log(`[StrategyV2Coordinator] Found ${steps.length} V2 screens`);

    steps.forEach((step: StepNode) => {
      const flowStep = v2ScreenAdapter.toFlowEntity(step);
      v2ScreenRegistry.register(flowStep);
    });

    await this.loadLaunchScreenId();
    console.log(`[StrategyV2Coordinator] Loaded ${steps.length} screens, launchScreenId: ${this.launchScreenId}`);
  }

  private async loadLaunchScreenId(): Promise<void> {
    if (!this.blockService) return;
    try {
      const block = await this.blockService.get(this.blockId);
      this.launchScreenId = block?.firstScreenId || null;
    } catch (error) {
      console.error('[StrategyV2Coordinator] Failed to load firstScreenId:', error);
    }
  }

  setCanvasHandle(handle: ReactFlowCanvasHandle | null): void {
    this.canvasHandle = handle;
    if (handle && this.initialized) {
      this.loadExistingScreensToCanvas();
    }
  }

  setLoadingCallbacks(callbacks: LoadingCallbacks): void {
    this.loadingCallbacks = callbacks;
  }

  private async wrapWithLoading<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    try {
      this.loadingCallbacks?.onSyncStart?.(operation);
      const result = await fn();
      this.loadingCallbacks?.onSyncEnd?.();
      return result;
    } catch (error) {
      this.loadingCallbacks?.onSyncError?.(error as Error, operation);
      this.loadingCallbacks?.onSyncEnd?.();
      throw error;
    }
  }

  async getLaunchEntityId(): Promise<string | null> {
    if (this.launchScreenId) return this.launchScreenId;
    await this.loadLaunchScreenId();
    return this.launchScreenId;
  }

  async setLaunchEntity(entityId: string): Promise<{
    deletedTransitionIds: string[];
    affectedEntitiesBackup: Map<string, FlowStepNode>;
  }> {
    return this.wrapWithLoading('Setting launch screen', async () => {
      const previousLaunchId = this.launchScreenId;

      const step = v2ScreenRegistry.get(entityId);
      if (!step) throw new Error(`Screen not found: ${entityId}`);

      // Find and delete incoming edges
      const allTransitions = this.computeAllTransitions();
      const incomingTransitions = allTransitions.filter(t => t.targetEntityId === entityId);
      const deletedTransitionIds = incomingTransitions.map(t => t.id);

      const affectedEntitiesBackup = new Map<string, FlowStepNode>();
      for (const transition of incomingTransitions) {
        const sourceStep = v2ScreenRegistry.get(transition.sourceEntityId);
        if (sourceStep) {
          affectedEntitiesBackup.set(transition.sourceEntityId, JSON.parse(JSON.stringify(sourceStep)));
        }
      }

      if (deletedTransitionIds.length > 0) {
        await this.deleteTransitions(deletedTransitionIds);
      }

      // Update block.firstScreenId via StrategyBlockService
      if (this.blockService) {
        await this.blockService.update(this.blockId, { firstScreenId: entityId } as any);
      }

      this.launchScreenId = entityId;
      this.onLaunchScreenChanged?.(entityId);

      if (this.canvasHandle) {
        if (previousLaunchId && previousLaunchId !== entityId) {
          this.canvasHandle.updateNodeData(previousLaunchId, { isLaunchScreen: false });
        }
        this.canvasHandle.updateNodeData(entityId, { isLaunchScreen: true });
      }

      return { deletedTransitionIds, affectedEntitiesBackup };
    });
  }

  // ============================================================================
  // FLOWCOORDINATOR INTERFACE
  // ============================================================================

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async initialize(_projectId?: string, _strategyId?: string, _blockId?: string, launchScreenId?: string): Promise<void> {
    if (launchScreenId) this.launchScreenId = launchScreenId;
  }

  getAllEntities(): FlowStepNode[] {
    return v2ScreenRegistry.getAll();
  }

  private generateUniqueName(baseName: string): string {
    const allEntities = v2ScreenRegistry.getAll();
    const usedNames = new Set(allEntities.map(e => e.name || ''));
    if (!usedNames.has(baseName)) return baseName;
    let counter = 2;
    while (usedNames.has(`${baseName} ${counter}`)) counter++;
    return `${baseName} ${counter}`;
  }

  async createEntity(data: Partial<FlowStepNode>, position: { x: number; y: number }): Promise<CreateEntityResult<FlowStepNode>> {
    const isFirstScreen = v2ScreenRegistry.getAll().length === 0;
    const templateId = (data as any).templateId || (data as any).id;

    if (templateId && this.templateSource) {
      const template = this.templateSource(templateId);

      if (template) {
        // FlexibleLayout stub
        if ((template as any).screenType === 'ScreenTitleSubtitlePicker') {
          const flexId = crypto.randomUUID();
          const flexStep: FlowStepNode = {
            id: flexId, entityId: flexId, position,
            screenType: 'FlexibleLayout' as any,
            name: this.generateUniqueName('Flexible Layout'),
            tags: [],
            content: {
              flexibleLayout: { scrollable: true, sections: [] },
              canvas: { background: { kind: 'solid', content: { color: '#ffffff' } } },
            } as any,
          };

          const enrichedFlex = this.enrichWithAiContent(flexStep);
          await this.wrapWithLoading('Creating screen', async () => {
            const firebaseStep = v2ScreenAdapter.toFirebaseStep(enrichedFlex);
            await this.screenService!.addScreen(firebaseStep);
          });
          this.addStepToCanvas(enrichedFlex);
          if (isFirstScreen) await this.setLaunchEntity(flexId);
          return { entity: enrichedFlex };
        }

        let newStep = v2ScreenAdapter.createFromV2Template(template, position);

        newStep.name = this.generateUniqueName(newStep.name || 'Screen');
        const enrichedStep = this.enrichWithAiContent(newStep);

        await this.wrapWithLoading('Creating screen', async () => {
          const firebaseStep = v2ScreenAdapter.toFirebaseStep(enrichedStep);
          await this.screenService!.addScreen(firebaseStep);
        });
        this.addStepToCanvas(enrichedStep);
        if (isFirstScreen) await this.setLaunchEntity(enrichedStep.entityId);
        return { entity: enrichedStep };
      }
    }

    if (!('content' in data) || typeof data.screenType !== 'string') {
      throw new Error('Strategy V2 screen creation requires a native V2 StepNode template');
    }

    const newStep = v2ScreenAdapter.createFromV2Template(data as StepNode, position);
    const enrichedFallback = this.enrichWithAiContent(newStep);
    await this.wrapWithLoading('Creating screen', async () => {
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(enrichedFallback);
      await this.screenService!.addScreen(firebaseStep);
    });
    this.addStepToCanvas(enrichedFallback);
    if (isFirstScreen) await this.setLaunchEntity(enrichedFallback.entityId);
    return { entity: enrichedFallback };
  }

  updateEntity(entityId: string, updates: Partial<FlowStepNode>): void {
    const current = v2ScreenRegistry.get(entityId);
    if (!current) return;
    v2ScreenRegistry.update(entityId, { ...current, ...updates });
  }

  async restoreEntities(steps: FlowStepNode[], affectedStepsBackup?: Map<string, FlowStepNode>): Promise<void> {
    for (const step of steps) {
      this.addStepToCanvas(step);
      await this.wrapWithLoading('Restoring screen', async () => {
        const firebaseStep = v2ScreenAdapter.toFirebaseStep(step);
        await this.screenService!.addScreen(firebaseStep);
      });
    }

    if (affectedStepsBackup && affectedStepsBackup.size > 0) {
      for (const [stepId, originalStep] of affectedStepsBackup) {
        v2ScreenRegistry.update(stepId, originalStep);
        await this.wrapWithLoading('Restoring connections', async () => {
          const firebaseStep = v2ScreenAdapter.toFirebaseStep(originalStep);
          await this.screenService!.replaceScreen(stepId, firebaseStep);
        });
      }
    }

    const restoredTransitions = this.computeTransitionsFromSteps(steps);
    this.syncEdgesToCanvas(restoredTransitions);
  }

  async deleteEntities(stepIds: string[]): Promise<{
    deletedTransitionIds: string[];
    affectedScreensBackup: Map<string, FlowStepNode>;
  }> {
    const allTransitions = this.computeAllTransitions();
    const deletedTransitionIds = allTransitions
      .filter(t => stepIds.includes(t.sourceEntityId) || stepIds.includes(t.targetEntityId))
      .map(t => t.id);

    const affectedScreensBackup = await this.cleanupIncomingConnections(stepIds);

    stepIds.forEach(id => v2ScreenRegistry.delete(id));
    this.executeCanvasOperation(handle => handle.removeNodes(stepIds));
    this.syncEdgesToCanvas();

    await this.wrapWithLoading(`Deleting ${stepIds.length} screen(s)`, async () => {
      await Promise.all(stepIds.map(id => this.screenService!.deleteScreen(id)));
    });

    return { deletedTransitionIds, affectedScreensBackup };
  }

  async updateEntityPositions(moves: EntityMove[]): Promise<void> {
    const normalizedMoves = moves.map(move => ({
      entityId: move.entityId,
      position: { x: move.newPosition.x ?? 0, y: move.newPosition.y ?? 0 }
    }));

    normalizedMoves.forEach(({ entityId, position }) => {
      v2ScreenRegistry.updatePosition(entityId, position);
    });

    // Position-only updates use merge (safe for top-level fields)
    if (this.screenService) {
      await this.wrapWithLoading(`Moving ${moves.length} screen(s)`, async () => {
        // For strategy service, we need getScreen + replaceScreen since there's no updateScreen with merge
        // But position updates are safe as top-level fields, so we read-modify-write
        for (const { entityId, position } of normalizedMoves) {
          const current = await this.screenService!.getScreen(entityId);
          if (current) {
            await this.screenService!.replaceScreen(entityId, { ...current, position } as any);
          }
        }
      });
    }

    this.executeCanvasOperation(handle =>
      handle.updateNodePositions(moves.map(m => ({
        entityId: m.entityId,
        oldPosition: m.oldPosition,
        newPosition: m.newPosition
      })))
    );
  }

  async cloneEntity(sourceEntityId: string, offset: PositionOnConfigurator): Promise<CreateEntityResult<FlowStepNode>> {
    const sourceStep = v2ScreenRegistry.get(sourceEntityId);
    if (!sourceStep) throw new Error(`Screen not found: ${sourceEntityId}`);

    const newPosition = {
      x: (sourceStep.position?.x || 0) + (offset.x ?? 0),
      y: (sourceStep.position?.y || 0) + (offset.y ?? 0)
    };

    const cloned = JSON.parse(JSON.stringify(sourceStep)) as FlowStepNode;
    const withoutEdges = clearAllStepEdges(cloned as StepNode);
    const uuid = crypto.randomUUID();

    const clonedStep: FlowStepNode = {
      ...(withoutEdges as FlowStepNode),
      id: uuid, entityId: uuid, position: newPosition,
      name: `${sourceStep.name || 'Screen'} (Copy)`,
    };

    await this.wrapWithLoading('Cloning screen', async () => {
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(clonedStep);
      await this.screenService!.addScreen(firebaseStep);
    });
    this.addStepToCanvas(clonedStep);
    return { entity: clonedStep };
  }

  async addPreparedScreens(steps: FlowStepNode[]): Promise<void> {
    for (const step of steps) {
      this.addStepToCanvas(step);
      await this.wrapWithLoading('Pasting screen', async () => {
        const firebaseStep = v2ScreenAdapter.toFirebaseStep(step);
        await this.screenService!.addScreen(firebaseStep);
      });
    }
    this.syncEdgesToCanvas();
  }

  async updateEntityAndRefreshConnections(entityId: string, updatedStep: FlowStepNode): Promise<void> {
    // Always re-extract aiContent so AI sees the latest content on regeneration
    const enriched = this.enrichWithAiContent(updatedStep);

    v2ScreenRegistry.update(entityId, enriched);

    if (this.screenService) {
      await this.wrapWithLoading('Updating screen', async () => {
        // Re-fetch the latest aiAssets from Firestore BEFORE overwriting.
        // The backend writes generate-image results directly to
        // `aiAssets.{slot}.experiments.{id}.generatedUrl` via async updateDoc,
        // and replaceScreen() below does a full setDoc() overwrite — without
        // this merge, a save issued while (or shortly after) a generation
        // completes would clobber the backend's generatedUrl / status='completed'
        // writes with the user's stale in-memory snapshot.
        let firebaseStep = v2ScreenAdapter.toFirebaseStep(enriched);
        try {
          const latest = await this.screenService!.getScreen(entityId);
          const latestAiAssets = (latest as any)?.aiAssets;
          if (latestAiAssets && typeof latestAiAssets === 'object') {
            firebaseStep = { ...(firebaseStep as any), aiAssets: latestAiAssets } as StepNode;
          }
        } catch (err) {
          console.warn('[StrategyV2ScreenFlowCoordinator] Failed to refetch aiAssets before save, continuing with local state:', err);
        }
        await this.screenService!.replaceScreen(entityId, firebaseStep);
      });
    }

    const handles = buildV2ActionHandles(enriched);
    this.executeCanvasOperation(handle => handle.updateNodeData(entityId, { handles }));

    const allEdges = this.computeAllTransitions();
    this.executeCanvasOperation(handle => handle.syncEdges(allEdges));
  }

  // ============================================================================
  // TRANSITION OPERATIONS
  // ============================================================================

  async createTransition(input: CreateTransitionInput): Promise<EntityTransition> {
    const { sourceEntityId, targetEntityId, sourceActionKey, label } = input;
    const sourceStep = v2ScreenRegistry.get(sourceEntityId);
    if (!sourceStep) throw new Error(`Source screen not found: ${sourceEntityId}`);
    if (!v2ScreenRegistry.has(targetEntityId)) throw new Error(`Target screen not found: ${targetEntityId}`);
    if (sourceEntityId === targetEntityId) throw new Error('Cannot create transition to same screen');

    const transitionId = TransitionIdGenerator.generate(sourceEntityId, sourceActionKey, targetEntityId);
    const transition: EntityTransition = { id: transitionId, sourceEntityId, targetEntityId, sourceActionKey, label: label || '' };

    const updatedStepNode = v2TransitionManager.addTransition(sourceStep as StepNode, transition);
    v2ScreenRegistry.update(sourceEntityId, { ...updatedStepNode, entityId: sourceStep.entityId } as FlowStepNode);
    this.executeCanvasOperation(handle => handle.addEdge(this.createEdgeData(transition)));

    await this.wrapWithLoading('Creating transition', async () => {
      const registryStep = v2ScreenRegistry.get(sourceEntityId)!;
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(registryStep);
      await this.screenService!.replaceScreen(sourceEntityId, firebaseStep);
    });

    return transition;
  }

  async updateTransition(transitionId: string, updates: Partial<Omit<EntityTransition, 'id'>>): Promise<EntityTransition> {
    const found = this.findTransitionOwner(transitionId);
    if (!found) throw new Error(`Transition not found: ${transitionId}`);

    const { step: ownerStep, transition: existingTransition } = found;
    const updatedStepNode = v2TransitionManager.updateTransition(ownerStep as StepNode, transitionId, updates);
    v2ScreenRegistry.update(ownerStep.entityId, { ...updatedStepNode, entityId: ownerStep.entityId } as FlowStepNode);

    const updatedTransition: EntityTransition = { ...existingTransition, ...updates };
    this.executeCanvasOperation(handle => handle.updateEdge(this.createEdgeData(updatedTransition)));

    await this.wrapWithLoading('Updating transition', async () => {
      const registryStep = v2ScreenRegistry.get(ownerStep.entityId)!;
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(registryStep);
      await this.screenService!.replaceScreen(ownerStep.id!, firebaseStep);
    });

    return updatedTransition;
  }

  async deleteTransition(transitionId: string): Promise<void> {
    const found = this.findTransitionOwner(transitionId);
    if (!found) throw new Error(`Transition not found: ${transitionId}`);

    const { step: ownerStep } = found;
    const updatedStepNode = v2TransitionManager.removeTransition(ownerStep as StepNode, transitionId);
    v2ScreenRegistry.update(ownerStep.entityId, { ...updatedStepNode, entityId: ownerStep.entityId } as FlowStepNode);
    this.executeCanvasOperation(handle => handle.removeEdge(transitionId));

    await this.wrapWithLoading('Deleting transition', async () => {
      const registryStep = v2ScreenRegistry.get(ownerStep.entityId)!;
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(registryStep);
      await this.screenService!.replaceScreen(ownerStep.id!, firebaseStep);
    });
  }

  async deleteTransitions(transitionIds: string[]): Promise<void> {
    const stepUpdates = new Map<string, FlowStepNode>();
    const allSteps = v2ScreenRegistry.getAll();

    for (const transitionId of transitionIds) {
      for (const step of allSteps) {
        const transitions = v2TransitionManager.extractTransitions(step as StepNode);
        if (transitions.some(t => t.id === transitionId)) {
          const currentStep = stepUpdates.get(step.entityId) || step;
          const updatedStepNode = v2TransitionManager.removeTransition(currentStep as StepNode, transitionId);
          stepUpdates.set(step.entityId, { ...updatedStepNode, entityId: step.entityId } as FlowStepNode);
          break;
        }
      }
    }

    stepUpdates.forEach((step, entityId) => v2ScreenRegistry.update(entityId, step));
    this.executeCanvasOperation(handle => handle.removeEdges(transitionIds));

    await this.wrapWithLoading(`Deleting ${transitionIds.length} transition(s)`, async () => {
      await Promise.all(
        Array.from(stepUpdates.entries()).map(([entityId, step]) => {
          const firebaseStep = v2ScreenAdapter.toFirebaseStep(step);
          return this.screenService!.replaceScreen(entityId, firebaseStep);
        })
      );
    });
  }

  getAllTransitions(): EntityTransition[] { return this.computeAllTransitions(); }
  getTransition(transitionId: string): EntityTransition | undefined {
    return this.computeAllTransitions().find(t => t.id === transitionId);
  }

  // ============================================================================
  // ACCESSORS
  // ============================================================================

  getCanvasHandle(): ReactFlowCanvasHandle | null { return this.canvasHandle; }
  getEntity(id: string): FlowStepNode | undefined { return v2ScreenRegistry.get(id); }
  getScreen(screenId: string): FlowStepNode | undefined { return v2ScreenRegistry.get(screenId); }
  isInitialized(): boolean { return this.initialized; }

  destroy(): void {
    if (!this.screenService) return;
    console.log('[StrategyV2Coordinator] Destroying...');
    v2ScreenRegistry.clear();
    this.canvasHandle = null;
    this.screenService = null;
    this.blockService = null;
    this.onLaunchScreenChanged = undefined;
    this.initialized = false;
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  private loadExistingScreensToCanvas(): void {
    if (!this.canvasHandle) return;

    const stepIds = v2ScreenRegistry.getAllIds();
    stepIds.forEach((stepId) => {
      const step = v2ScreenRegistry.get(stepId);
      if (!step) return;
      const handles = buildV2ActionHandles(step);
      this.canvasHandle!.addNode({
        entityId: step.entityId, entityType: 'screen' as const,
        nodeType: 'onboarding-screen-v2' as const,
        position: step.position || { x: 0, y: 0 },
        handles, isLaunchScreen: step.entityId === this.launchScreenId,
      });
    });

    const allEdges = this.computeAllTransitions();
    this.canvasHandle!.syncEdges(allEdges);
  }

  /**
   * Enrich a FlowStepNode with aiContent extracted via SimplifiedV2Mapper.
   * Every strategy screen always has aiContent so AI can see it during regeneration.
   * Preserves existing aiMeta (doesn't overwrite AI generation metadata on manual edits).
   */
  private enrichWithAiContent(step: FlowStepNode): FlowStepNode {
    try {
      const simplified = simplifiedV2Mapper.toSimplified(step as unknown as StepNode, 'en');
      return { ...step, aiContent: simplified.content } as FlowStepNode;
    } catch (error) {
      console.warn('[StrategyV2Coordinator] Failed to extract aiContent:', error);
      return step;
    }
  }

  private addStepToCanvas(step: FlowStepNode): void {
    v2ScreenRegistry.register(step);
    if (this.canvasHandle) {
      const handles = buildV2ActionHandles(step);
      this.canvasHandle.addNode({
        entityId: step.entityId, entityType: 'screen',
        nodeType: 'onboarding-screen-v2',
        position: step.position || { x: 0, y: 0 },
        handles, isLaunchScreen: step.entityId === this.launchScreenId,
      });
    }
  }

  private syncEdgesToCanvas(edges?: EntityTransition[]): void {
    if (this.canvasHandle) {
      this.canvasHandle.syncEdges(edges ?? this.computeAllTransitions());
    }
  }

  private computeTransitionsFromSteps(steps: FlowStepNode[]): EntityTransition[] {
    const transitions: EntityTransition[] = [];
    steps.forEach(step => transitions.push(...v2TransitionManager.extractTransitions(step as StepNode)));
    return transitions;
  }

  private computeAllTransitions(): EntityTransition[] {
    return this.computeTransitionsFromSteps(v2ScreenRegistry.getAll());
  }

  private executeCanvasOperation<T>(fn: (handle: ReactFlowCanvasHandle) => T): T | void {
    if (!this.canvasHandle) return;
    return fn(this.canvasHandle);
  }

  private findTransitionOwner(transitionId: string): { step: FlowStepNode; transition: EntityTransition } | undefined {
    for (const step of v2ScreenRegistry.getAll()) {
      const transitions = v2TransitionManager.extractTransitions(step as StepNode);
      const transition = transitions.find(t => t.id === transitionId);
      if (transition) return { step, transition };
    }
    return undefined;
  }

  private async cleanupIncomingConnections(deletedStepIds: string[]): Promise<Map<string, FlowStepNode>> {
    const affectedStepsBackup = new Map<string, FlowStepNode>();
    const allTransitions = this.computeAllTransitions();
    const incomingTransitions = allTransitions.filter(t =>
      deletedStepIds.includes(t.targetEntityId) && !deletedStepIds.includes(t.sourceEntityId)
    );

    const transitionsBySource = new Map<string, string[]>();
    for (const transition of incomingTransitions) {
      if (!transitionsBySource.has(transition.sourceEntityId)) {
        transitionsBySource.set(transition.sourceEntityId, []);
      }
      transitionsBySource.get(transition.sourceEntityId)!.push(transition.id);
    }

    for (const [sourceStepId, transitionIds] of transitionsBySource) {
      const step = v2ScreenRegistry.get(sourceStepId);
      if (!step) continue;

      affectedStepsBackup.set(sourceStepId, JSON.parse(JSON.stringify(step)));

      let currentStep = step as StepNode;
      for (const transitionId of transitionIds) {
        currentStep = v2TransitionManager.removeTransition(currentStep, transitionId);
      }
      v2ScreenRegistry.update(sourceStepId, { ...currentStep, entityId: step.entityId } as FlowStepNode);

      if (this.screenService) {
        const firebaseStep = v2ScreenAdapter.toFirebaseStep(v2ScreenRegistry.get(sourceStepId)!);
        await this.screenService.replaceScreen(sourceStepId, firebaseStep);
      }
    }

    return affectedStepsBackup;
  }

  private createEdgeData(transition: EntityTransition): EdgeData {
    return {
      id: transition.id,
      source: transition.sourceEntityId,
      target: transition.targetEntityId,
      sourceHandle: TransitionIdGenerator.generateHandleId(transition.sourceEntityId, transition.sourceActionKey),
      label: transition.label ?? '',
      type: 'transition',
      animated: false,
      data: {
        sourceEntityId: transition.sourceEntityId,
        targetEntityId: transition.targetEntityId,
        sourceActionKey: transition.sourceActionKey,
        rules: transition.rules || [],
      }
    };
  }

  debug(): void {
    console.log('[StrategyV2Coordinator] Debug:');
    console.log(`  projectId: ${this.projectId}`);
    console.log(`  strategyId: ${this.strategyId}`);
    console.log(`  blockId: ${this.blockId}`);
    console.log(`  launchScreenId: ${this.launchScreenId}`);
    const transitions = this.computeAllTransitions();
    console.log(`  Transitions: ${transitions.length}`);
    v2ScreenRegistry.debug();
  }
}
